import { useState } from 'react';
import { useAuth } from '../../../lib/auth-context.js';
import { useDashboardSummary } from '../../dashboard/hooks.js';
import { useScenariosList } from '../../scenario/hooks.js';

/**
 * Planning › Snapshot. Bundles the engine reads currently visible
 * to the SPA into a single downloadable JSON file. Operators use it
 * to attach state to support tickets without piecing together
 * multiple endpoint dumps.
 */
export function PlanningSnapshotPage(): JSX.Element {
  const { session } = useAuth();
  const { data: summary } = useDashboardSummary();
  const { data: scenarios } = useScenariosList();
  const [busy, setBusy] = useState(false);

  function download(): void {
    setBusy(true);
    const snapshot = {
      generatedAt: new Date().toISOString(),
      session: session
        ? {
            licenseId: session.licenseId,
            clientId: session.clientId,
            workfrontUserId: session.workfrontUserId,
            email: session.email,
            roles: session.roles,
          }
        : null,
      summary: summary ?? null,
      scenarios: scenarios ?? null,
    };
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wf-coe-snapshot-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setBusy(false);
  }

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Planning · Snapshot</h1>
        <p className="text-sm text-foreground/60">
          Download the current engine state as a single JSON file. Useful for support tickets and
          offline review.
        </p>
      </header>
      <div className="px-6 py-3">
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <button
            type="button"
            onClick={download}
            disabled={busy || !summary}
            className="rounded-md bg-[color:var(--brand-yellow)] px-4 py-2 text-sm font-medium text-[color:var(--gradient-end)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Preparing…' : 'Download snapshot.json'}
          </button>
          <p className="mt-3 text-xs text-foreground/55">
            Snapshot contents: session identity, dashboard summary, scenario list. Stage 2 adds KPI
            values + recent system logs to the bundle.
          </p>
        </section>
      </div>
    </div>
  );
}
